import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import AdminLayout from '../AdminLayout';
import { API } from '../../../lib/api';
import { toast } from 'sonner';
import {
  RefreshCw, Calendar, Clock, Play, Pause, Trash2, Eye,
  MessageSquare, Bell, Mail, User, ChevronRight, Settings,
  Power, AlertTriangle, CheckCircle2, XCircle, SkipForward,
  CalendarClock, Phone, FileText, Shield, Ban, Copy,
  ChevronDown, ChevronUp
} from 'lucide-react';

// Event labels in Italian
const EVENT_LABELS = {
  'booking_confirmed': 'Conferma Prenotazione',
  'arrival_3_days': 'Promemoria Arrivo (3gg)',
  'arrival_today': 'Promemoria Arrivo (oggi)',
  'arrival_reminder': 'Promemoria Arrivo',
  'review_request': 'Richiesta Recensione',
};

// Channel icon
const ChannelIcon = ({ channel }) => {
  const icons = {
    whatsapp: MessageSquare,
    push: Bell,
    email: Mail,
  };
  const Icon = icons[channel] || MessageSquare;
  return <Icon className="w-4 h-4" />;
};

// Validation Status Badge
const ValidationBadge = ({ status }) => {
  if (status === 'VALID') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
        <CheckCircle2 className="w-3 h-3" /> Valido
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
      <XCircle className="w-3 h-3" /> Non valido
    </span>
  );
};

// Simulated Message Card - Detailed
const SimulatedMessageCard = ({ message, onExpand, expanded }) => {
  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={onExpand}
        data-testid={`sim-message-${message.booking_code}`}
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
            {message.guest_name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div>
            <p className="font-medium text-gray-900">{message.guest_name || 'Ospite'}</p>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="font-mono">{message.booking_code}</span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {message.phone_masked}
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm font-medium text-gray-900">{message.scheduled_display}</p>
            <p className="text-xs text-gray-500">{EVENT_LABELS[message.event] || message.event}</p>
          </div>
          <ValidationBadge status={message.validation_status} />
          <div className="flex items-center gap-1">
            <ChannelIcon channel={message.channel} />
            <span className="text-xs text-gray-500 capitalize">{message.channel}</span>
          </div>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>
      
      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-4">
          {/* Booking Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-xs text-gray-500 mb-1">Arrivo</p>
              <p className="font-medium text-gray-900">{message.arrival_date || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Partenza</p>
              <p className="font-medium text-gray-900">{message.departure_date || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Stato Prenotazione</p>
              <p className="font-medium text-gray-900 capitalize">{message.booking_status || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Template</p>
              <p className="font-medium text-gray-900">{message.template_name}</p>
            </div>
          </div>
          
          {/* Idempotency Key */}
          <div className="flex items-center gap-2 text-xs">
            <Shield className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500">Chiave Idempotenza:</span>
            <code className="bg-gray-200 px-2 py-0.5 rounded font-mono">{message.idempotency_key}</code>
          </div>
          
          {/* Rendered Message Preview */}
          <div>
            <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
              <FileText className="w-3 h-3" /> Anteprima Messaggio Renderizzato
            </p>
            <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-wrap max-h-64 overflow-y-auto">
              {message.rendered_message}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Excluded Message Card
const ExcludedMessageCard = ({ message }) => {
  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
          <Ban className="w-4 h-4 text-gray-500" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-700">{message.guest_name}</p>
          <p className="text-xs text-gray-500">{message.booking_code} • {EVENT_LABELS[message.event] || message.event}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-xs text-red-600 font-medium">{message.reason?.split(':')[0]}</p>
        <p className="text-xs text-gray-500">{message.reason?.split(':')[1]?.trim()}</p>
      </div>
    </div>
  );
};

// Day Section for Simulated Messages
const SimulatedDaySection = ({ title, messages, expandedId, setExpandedId }) => {
  if (!messages || messages.length === 0) return null;
  
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
          {messages.length} messaggi
        </span>
      </div>
      <div className="space-y-2">
        {messages.map((msg, idx) => (
          <SimulatedMessageCard 
            key={`${msg.booking_code}-${msg.event}-${idx}`}
            message={msg}
            expanded={expandedId === `${msg.booking_code}-${msg.event}-${idx}`}
            onExpand={() => setExpandedId(
              expandedId === `${msg.booking_code}-${msg.event}-${idx}` 
                ? null 
                : `${msg.booking_code}-${msg.event}-${idx}`
            )}
          />
        ))}
      </div>
    </div>
  );
};

// Stats Summary Card
const StatsSummaryCard = ({ stats }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Riepilogo Simulazione</h3>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <p className="text-3xl font-bold text-blue-600">{stats.total_bookings_evaluated}</p>
          <p className="text-xs text-gray-600 mt-1">Prenotazioni Valutate</p>
        </div>
        <div className="text-center p-4 bg-green-50 rounded-lg">
          <p className="text-3xl font-bold text-green-600">{stats.total_messages_simulated}</p>
          <p className="text-xs text-gray-600 mt-1">Messaggi Simulati</p>
        </div>
        <div className="text-center p-4 bg-orange-50 rounded-lg">
          <p className="text-3xl font-bold text-orange-600">{stats.total_excluded}</p>
          <p className="text-xs text-gray-600 mt-1">Esclusi</p>
        </div>
        <div className="text-center p-4 bg-purple-50 rounded-lg">
          <p className="text-3xl font-bold text-purple-600">{stats.total_duplicates_prevented}</p>
          <p className="text-xs text-gray-600 mt-1">Duplicati Prevenuti</p>
        </div>
      </div>
      
      {/* By Event */}
      <div className="border-t border-gray-100 pt-4">
        <p className="text-sm font-medium text-gray-700 mb-3">Messaggi per Regola:</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(stats.by_event || {}).map(([event, count]) => (
            <div key={event} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg">
              <span className="text-sm text-gray-700">{EVENT_LABELS[event] || event}:</span>
              <span className="font-bold text-gray-900">{count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Rules Stats Card
const RulesStatsCard = ({ statsByRule }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistiche per Regola</h3>
      
      <div className="space-y-3">
        {Object.entries(statsByRule || {}).map(([event, stats]) => (
          <div key={event} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">{stats.rule_name || EVENT_LABELS[event] || event}</p>
              <p className="text-xs text-gray-500">{event}</p>
            </div>
            <div className="flex gap-4 text-sm">
              <div className="text-center">
                <p className="font-bold text-green-600">{stats.simulated}</p>
                <p className="text-xs text-gray-500">Simulati</p>
              </div>
              <div className="text-center">
                <p className="font-bold text-orange-600">{stats.excluded}</p>
                <p className="text-xs text-gray-500">Esclusi</p>
              </div>
              <div className="text-center">
                <p className="font-bold text-purple-600">{stats.duplicates}</p>
                <p className="text-xs text-gray-500">Duplicati</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Scheduler Status Panel
const SchedulerStatusPanel = ({ status }) => {
  if (!status) return null;
  
  const isSimulation = status.simulation_mode;
  const isAutoSend = status.auto_send_enabled;
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Power className={`w-5 h-5 ${status.is_running ? 'text-green-500' : 'text-gray-400'}`} />
          <span className="font-semibold text-gray-900">Scheduler Engine</span>
          {status.is_running ? (
            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Attivo</span>
          ) : (
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">Inattivo</span>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className={`p-3 rounded-lg ${isSimulation ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
          <div className="flex items-center gap-2 mb-1">
            {isSimulation ? (
              <AlertTriangle className="w-4 h-4 text-amber-600" />
            ) : (
              <CheckCircle2 className="w-4 h-4 text-green-600" />
            )}
            <span className="text-xs font-medium text-gray-600">Modalità</span>
          </div>
          <p className={`text-sm font-bold ${isSimulation ? 'text-amber-700' : 'text-green-700'}`}>
            {isSimulation ? 'SIMULAZIONE' : 'PRODUZIONE'}
          </p>
        </div>
        
        <div className={`p-3 rounded-lg ${isAutoSend ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
          <div className="flex items-center gap-2 mb-1">
            {isAutoSend ? (
              <CheckCircle2 className="w-4 h-4 text-green-600" />
            ) : (
              <Pause className="w-4 h-4 text-gray-500" />
            )}
            <span className="text-xs font-medium text-gray-600">Auto-Invio</span>
          </div>
          <p className={`text-sm font-bold ${isAutoSend ? 'text-green-700' : 'text-gray-600'}`}>
            {isAutoSend ? 'ATTIVO' : 'DISATTIVO'}
          </p>
        </div>
        
        <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
          <div className="flex items-center gap-2 mb-1">
            <MessageSquare className="w-4 h-4 text-blue-600" />
            <span className="text-xs font-medium text-gray-600">In Coda</span>
          </div>
          <p className="text-sm font-bold text-blue-700">
            {status.queue_stats?.queued || 0}
          </p>
        </div>
        
        <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-gray-500" />
            <span className="text-xs font-medium text-gray-600">Ultima Exec</span>
          </div>
          <p className="text-xs font-medium text-gray-700">
            {status.last_execution?.completed_at 
              ? new Date(status.last_execution.completed_at).toLocaleTimeString('it-IT', {
                  hour: '2-digit', minute: '2-digit'
                })
              : 'N/A'
            }
          </p>
        </div>
      </div>
    </div>
  );
};

// Live Send Confirmation
const LiveSendConfirmation = ({ liveMessagesSent }) => {
  return (
    <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
      <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" />
      <div>
        <p className="font-semibold text-green-800">Nessun Messaggio Live Inviato</p>
        <p className="text-sm text-green-700">
          Questa è una simulazione. <strong>{liveMessagesSent}</strong> messaggi reali sono stati inviati.
          Il provider MYRC non è stato contattato.
        </p>
      </div>
    </div>
  );
};

// Production Config Box
const ProductionConfigBox = () => {
  const config = `// Per abilitare l'invio automatico live:
await updateSchedulerSettings({
  simulation_mode: false,
  auto_send_enabled: true
});

// ATTENZIONE: Richiede approvazione esplicita!`;

  return (
    <div className="bg-gray-800 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-gray-300">
          Configurazione per abilitare invio live (non eseguire senza approvazione):
        </p>
        <button 
          onClick={() => {
            navigator.clipboard.writeText(config);
            toast.success('Configurazione copiata');
          }}
          className="p-1.5 hover:bg-gray-700 rounded transition-colors"
        >
          <Copy className="w-4 h-4 text-gray-400" />
        </button>
      </div>
      <pre className="text-xs text-green-400 font-mono overflow-x-auto">{config}</pre>
    </div>
  );
};

export default function SimulationPage() {
  const { token } = useAuth();
  const [simulation, setSimulation] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [daysAhead, setDaysAhead] = useState(7);
  const [expandedId, setExpandedId] = useState(null);
  const [showExcluded, setShowExcluded] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);

  const fetchSimulation = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/communication/simulation/advanced?days_ahead=${daysAhead}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSimulation(data);
      } else {
        toast.error('Errore nel caricamento simulazione');
      }
    } catch (error) {
      console.error('Error fetching simulation:', error);
      toast.error('Errore di rete');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      const response = await fetch(`${API}/admin/communication/scheduler/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSchedulerStatus(data);
      }
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
    }
  };

  useEffect(() => {
    if (token) {
      fetchSimulation();
      fetchSchedulerStatus();
    }
  }, [token, daysAhead]);

  // Refresh scheduler status when page becomes visible (user returns from Settings)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && token) {
        fetchSchedulerStatus();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [token]);

  // Also refresh periodically every 30 seconds
  useEffect(() => {
    if (!token) return;
    
    const interval = setInterval(() => {
      fetchSchedulerStatus();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [token]);

  const totalSimulated = simulation?.summary?.total_messages_simulated || 0;
  const todayCount = simulation?.simulated_messages?.today?.length || 0;
  const tomorrowCount = simulation?.simulated_messages?.tomorrow?.length || 0;
  const nextDaysCount = simulation?.simulated_messages?.next_days?.length || 0;

  return (
    <AdminLayout title="Simulation Center">
      <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Centro Simulazione Avanzata</h1>
            <p className="text-gray-500 mt-1">
              Simulazione completa delle comunicazioni automatiche • Phase 5.2A
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <select
              value={daysAhead}
              onChange={(e) => setDaysAhead(parseInt(e.target.value))}
              className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm"
              data-testid="days-selector"
            >
              <option value="1">Solo Oggi</option>
              <option value="2">Oggi + Domani</option>
              <option value="7">Prossimi 7 giorni</option>
              <option value="14">Prossimi 14 giorni</option>
              <option value="30">Prossimi 30 giorni</option>
            </select>
            <button
              onClick={() => { fetchSimulation(); fetchSchedulerStatus(); }}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              data-testid="run-simulation-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Simulando...' : 'Esegui Simulazione'}
            </button>
          </div>
        </div>

        {/* Scheduler Status */}
        <SchedulerStatusPanel status={schedulerStatus} />

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-gray-200 rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : simulation ? (
          <div className="space-y-6">
            {/* Live Send Confirmation */}
            <LiveSendConfirmation liveMessagesSent={simulation.live_messages_sent || 0} />
            
            {/* Stats Summary */}
            <StatsSummaryCard stats={simulation.summary || {}} />
            
            {/* Rules Stats */}
            {Object.keys(simulation.stats_by_rule || {}).length > 0 && (
              <RulesStatsCard statsByRule={simulation.stats_by_rule} />
            )}

            {/* Simulated Messages */}
            {totalSimulated > 0 ? (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Messaggi Simulati ({totalSimulated})
                </h2>
                
                <SimulatedDaySection 
                  title={`Oggi (${todayCount})`}
                  messages={simulation.simulated_messages?.today}
                  expandedId={expandedId}
                  setExpandedId={setExpandedId}
                />
                
                <SimulatedDaySection 
                  title={`Domani (${tomorrowCount})`}
                  messages={simulation.simulated_messages?.tomorrow}
                  expandedId={expandedId}
                  setExpandedId={setExpandedId}
                />
                
                <SimulatedDaySection 
                  title={`Prossimi Giorni (${nextDaysCount})`}
                  messages={simulation.simulated_messages?.next_days}
                  expandedId={expandedId}
                  setExpandedId={setExpandedId}
                />
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun messaggio da simulare</h3>
                <p className="text-gray-500">
                  Non ci sono prenotazioni che soddisfano i criteri delle regole abilitate
                </p>
              </div>
            )}

            {/* Excluded Messages */}
            {(simulation.excluded_messages?.length > 0) && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <button
                  onClick={() => setShowExcluded(!showExcluded)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Ban className="w-5 h-5 text-orange-500" />
                    <span className="font-semibold text-gray-900">
                      Messaggi Esclusi ({simulation.excluded_messages.length})
                    </span>
                  </div>
                  {showExcluded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </button>
                {showExcluded && (
                  <div className="border-t border-gray-100 p-4 space-y-2 max-h-96 overflow-y-auto">
                    {simulation.excluded_messages.map((msg, idx) => (
                      <ExcludedMessageCard key={idx} message={msg} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Duplicates Prevented */}
            {(simulation.duplicates_prevented?.length > 0) && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <button
                  onClick={() => setShowDuplicates(!showDuplicates)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Copy className="w-5 h-5 text-purple-500" />
                    <span className="font-semibold text-gray-900">
                      Duplicati Prevenuti ({simulation.duplicates_prevented.length})
                    </span>
                  </div>
                  {showDuplicates ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </button>
                {showDuplicates && (
                  <div className="border-t border-gray-100 p-4 space-y-2 max-h-96 overflow-y-auto">
                    {simulation.duplicates_prevented.map((msg, idx) => (
                      <ExcludedMessageCard key={idx} message={msg} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Production Config - Only show if in simulation mode */}
            {schedulerStatus?.simulation_mode && (
              <ProductionConfigBox />
            )}

            {/* Safety Notice - Dynamic based on actual mode */}
            {schedulerStatus?.simulation_mode ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Modalità Simulazione Attiva</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Il sistema è in <strong>modalità simulazione</strong>. Nessun messaggio viene inviato automaticamente.
                      Per abilitare l&apos;invio automatico live, vai in Impostazioni Automazione.
                    </p>
                  </div>
                </div>
              </div>
            ) : schedulerStatus?.auto_send_enabled ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Modalità Produzione Attiva</p>
                    <p className="text-sm text-green-700 mt-1">
                      Il sistema è in <strong>modalità produzione</strong>. I messaggi vengono inviati automaticamente
                      ai nuovi ospiti idonei. Solo i messaggi in coda con status &quot;queued&quot; verranno elaborati.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <div className="flex gap-3">
                  <Pause className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Auto-Invio Disabilitato</p>
                    <p className="text-sm text-gray-600 mt-1">
                      L&apos;invio automatico è disabilitato. I messaggi vengono accodati ma non inviati automaticamente.
                      Abilita Auto-Invio nelle Impostazioni Automazione per attivare l&apos;invio.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-red-700">Errore nel caricamento della simulazione</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
